<script lang="ts" setup>
import { useI18n } from '#i18n';
import { XSpinner, XIcon } from '@clicksign/excalibur';

import { useCurrentSignatureRequestKey, useModals, toCapitalize, isFetchError } from '~~core';

import DigitalCertificateUnprocessedSignatureModal from '../DigitalCertificateUnprocessedSignatureModal/DigitalCertificateUnprocessedSignatureModal.vue';
import DigitalCertificateMissingCertificatesByDocumentationModal from '../DigitalCertificateMissingCertificatesByDocumentationModal/DigitalCertificateMissingCertificatesByDocumentationModal.vue';
import DigitalCertificateCloudUnavailableModal from '../DigitalCertificateCloudUnavailableModal/DigitalCertificateCloudUnavailableModal.vue';
import { DigitalCertificateCloudProviderData } from '../../schemas/digital-certificate';
import { useFetchSigner } from '../../repositories/signer';
import { discoverDigitalCertificateCloud, completeDigitalCertificateCloud } from '../../services';

const gtm = useGtm();

type DigitalCertificateCloudProvidersProps = {
  isReadyToComplete?: boolean;
};

const props = withDefaults(defineProps<DigitalCertificateCloudProvidersProps>(), {
  isReadyToComplete: false,
});

type DigitalCertificateCloudProvidersEmits = {
  (e: 'completed', certificateKey: string): void;
  (e: 'back'): void;
};

const emit = defineEmits<DigitalCertificateCloudProvidersEmits>();

const modalName = {
  missingCertificatesByDocumentation: 'missing-certificates-by-documentation',
  unavailable: 'unavailable',
  unprocessedSignature: 'unprocessed-signature',
};

const { closeModal, openModal, isModalOpen } = useModals();
const { signatureRequestKey } = useCurrentSignatureRequestKey();
const { signer } = useFetchSigner(signatureRequestKey);

const { t } = useI18n();
const title = t('requirement.digitalCertificate.cloud.title');

useHead({ title });

const providers = ref<DigitalCertificateCloudProviderData[]>();
const isLoading = ref(true);

async function searchProvidersByDocumentation() {
  try {
    isLoading.value = true;
    providers.value = await discoverDigitalCertificateCloud(signatureRequestKey.value);

    if (providers.value.length > 1) isLoading.value = false;
    else if (providers.value[0]) navigateTo(providers.value[0].authUrl, { external: true });
  } catch (error) {
    if (isFetchError(error) && error.statusCode === 422) openModal(modalName.missingCertificatesByDocumentation);
    else openModal(modalName.unavailable);
  }
}

async function completeSignature() {
  try {
    isLoading.value = true;

    const { certificateKey } = await completeDigitalCertificateCloud(signatureRequestKey.value);

    gtm?.trackEvent({
      event: 'ga-send',
      ga_event_category: 'sign-step-digital-certificate',
      ga_event_name: 'success-cloud-certificate',
    });

    emit('completed', certificateKey);
  } catch {
    openModal(modalName.unprocessedSignature);
  }
}

async function handleSignatureState() {
  if (!props.isReadyToComplete) await searchProvidersByDocumentation();
  else await completeSignature();
}

async function tryAgain() {
  closeModal();
  await handleSignatureState();
}

onMounted(async () => {
  await handleSignatureState();
});
</script>

<template>
  <section :class="$style.digitalCertificateCloud">
    <div
      v-if="isLoading"
      data-testid="digitalCertificateLoading"
    >
      <h2>{{ $t('common.loadingWithElipsis') }}</h2>
      <XSpinner size="large" />
    </div>

    <div v-else>
      <header :class="$style.header">
        <h2 data-testid="digitalCertificateProvidersTitle">
          {{ $t('requirement.digitalCertificate.cloud.subtitle') }}
        </h2>
        <p :class="$style.description">
          {{
            $t('requirement.digitalCertificate.cloud.description', {
              documentation: signer?.documentation,
            })
          }}
        </p>
      </header>

      <ul
        :class="$style.providers"
        data-testid="digitalCertificateProviderList"
      >
        <li
          v-for="(item, index) in providers"
          :key="index"
          :class="$style.item"
          data-testid="providerItem"
        >
          <a
            :class="$style.link"
            :href="item.authUrl"
            :data-testid="`digitalCertificate${toCapitalize(item.serviceInfo.service.name)}ProviderLink`"
          >
            <img
              :class="$style.image"
              :src="item.serviceInfo.badgeUrl"
            >

            <h5 :class="$style.name">
              {{ item.serviceInfo.service.name }}
            </h5>

            <XIcon
              :class="$style.icon"
              icon="external-link"
            />
          </a>
        </li>
      </ul>
    </div>

    <DigitalCertificateMissingCertificatesByDocumentationModal
      v-if="isModalOpen(modalName.missingCertificatesByDocumentation)"
      @close="emit('back')"
    />

    <DigitalCertificateCloudUnavailableModal
      v-if="isModalOpen(modalName.unavailable)"
      @try-again="tryAgain()"
      @close="emit('back')"
    />

    <DigitalCertificateUnprocessedSignatureModal
      v-if="isModalOpen(modalName.unprocessedSignature)"
      @try-again="tryAgain()"
      @back="emit('back')"
    />
  </section>
</template>

<style lang="scss" module>
.digitalCertificateCloud {
  margin-bottom: var(--space-regular);
}

.header {
  text-align: center;
}

.providers {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  gap: var(--space-small-x);
  list-style: none;
}

.link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--space-regular);
  gap: var(--space-regular);
  text-align: left;
  text-decoration: none;
  color: var(--color-neutral-1000);
  border: var(--border-width-small) solid var(--color-brand-primary-200);
  border-radius: var(--border-radius-medium);

  &:hover {
    border-color: var(--color-brand-primary-400);
    cursor: pointer;
  }
}

.image {
  width: 24px;
}

.name {
  flex: 1;
  margin: 0;
  text-transform: capitalize;
}

.icon {
  color: var(--color-brand-primary-400);
}
</style>
